import { Typography, Button } from "antd";
import { useCallback, useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
    deleteVehicle,
    setModalName,
    updateFirmwareVehicle,
    getDropdownFirmwares,
    updateFirmwareOnVehicles
} from "store/actions";
import { FormItem, SelectSearch } from "../components";
import { parseFirmwares } from "utils/parsers";

const { Text, Title } = Typography;

export default function FirmwareVehicleForm() {
    const {
        firmwares: { dropdownFirmwares },
    } = useSelector<AppState, AppState>((state) => state);

    const dispatch = useDispatch();

    const { selectedVehicle } = useSelector<AppState, AppState["vehicles"]>(
        (state) => state.vehicles
    );

    const firmwaresOptions = useMemo(
        () => parseFirmwares(dropdownFirmwares),
        [dropdownFirmwares]
    );

    // Find the matching option value from firmwaresOptions based on vehicle's current firmware
    const preSelectedFirmwareVersion = useMemo(() => {
        if (!selectedVehicle || !firmwaresOptions || firmwaresOptions.length === 0) {
            return '';
        }

        // Adjust property name if needed
        const currentVersion = selectedVehicle.firmwareVersion;

        if (!currentVersion) {
            return '';
        }

        // Find matching option - check value, label, and name properties
        const matchingOption = firmwaresOptions.find(
            (option: any) =>
                option.value === currentVersion ||
                option.label === currentVersion ||
                option.name === currentVersion ||
                // Also check if the label/name contains the version
                option.label?.includes(currentVersion) ||
                option.name?.includes(currentVersion)
        );

        // Debug: log to see what's happening
        console.log("Current Version:", currentVersion);
        console.log("Firmware Options:", firmwaresOptions);
        console.log("Matching Option:", matchingOption);

        if (matchingOption) {
            return matchingOption.value;
        }

        return '';
    }, [selectedVehicle, firmwaresOptions]);

    const [version, setVersion] = useState<string>('');

    // Update version state when preSelectedFirmwareVersion changes or firmwaresOptions loads
    useEffect(() => {
        if (preSelectedFirmwareVersion) {
            setVersion(preSelectedFirmwareVersion);
        }
    }, [preSelectedFirmwareVersion]);

    const onUpdateFirmware = useCallback(() => {
        if (!version) {
            console.warn("No firmware version selected");
            return;
        }
        dispatch(updateFirmwareOnVehicles(version, [selectedVehicle?.deviceIotId || ""]));
        onCancel();
    }, [dispatch, selectedVehicle?.deviceIotId, version]);

    const onCancel = useCallback(() => {
        dispatch(setModalName(undefined));
    }, [dispatch]);

    useEffect(() => {
        if (!dropdownFirmwares) {
            console.log("reached here", dropdownFirmwares);
            dispatch(getDropdownFirmwares());
        }
    }, [dispatch, dropdownFirmwares]);

    return (
        <section className="delete-modal">
            <Title level={5}>Update Firmware</Title>
            <Text type="secondary">
                Are you sure you want to update vehicle firmware for{" "}
                <strong>"{selectedVehicle?.licensePlate}"</strong>?
                <br /><br />
            </Text>
            <FormItem label="Firmware Versions">
                <SelectSearch
                    value={version}
                    items={firmwaresOptions}
                    setFieldValue={setVersion}
                    fieldName="Firmware Version"
                    placeholder="Select"
                    onChangeProp={(value: string) => {
                        setVersion(value);
                    }}
                />
            </FormItem>
            <div className="action-btn-container">
                <Button className="cancel-btn" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    danger
                    onClick={onUpdateFirmware}
                    disabled={!version}
                >
                    Update Firmware
                </Button>
            </div>
        </section>
    );
}